export const EVENT_WEEKLY_LAYOUT = {
  STAFF_NAME: "1",
  WEEKLY_CALENDAR: "2",
};

export const EVENT_WEEKLY_LAYOUT_LABEL_KEY = "view-options";

export const EVENT_WEEKLY_SORT = {
  DEFAULT: "default",
  STAFF_NAME_ASC: "nameAsc",
  STAFF_NAME_DESC: "nameDesc",
  EMPLOYMENT_LATEST: "employmentLatest",
  EMPLOYMENT_OLDEST: "employmentOldest",
  /** @deprecated Use STAFF_NAME_ASC — kept for storage migration */
  STAFF_NAME: "1",
  /** @deprecated Unused in staff-name weekly view */
  START_TIME: "2",
};

export const EVENT_WEEKLY_SORT_LABEL_KEY = "sort-by";

const LEGACY_SORT_TO_NAME_ASC = new Set([
  EVENT_WEEKLY_SORT.STAFF_NAME,
  EVENT_WEEKLY_SORT.START_TIME,
]);

/**
 * Resolves a persisted or incoming sort id to a supported weekly staff sort option.
 * Falls back to Staff Name (A-Z), the product default.
 * @param {string|null|undefined} sortId
 * @returns {string}
 */
export const resolveEventWeeklySort = (sortId) => {
  const validIds = new Set([
    EVENT_WEEKLY_SORT.DEFAULT,
    EVENT_WEEKLY_SORT.STAFF_NAME_ASC,
    EVENT_WEEKLY_SORT.STAFF_NAME_DESC,
    EVENT_WEEKLY_SORT.EMPLOYMENT_LATEST,
    EVENT_WEEKLY_SORT.EMPLOYMENT_OLDEST,
  ]);

  if (validIds.has(sortId)) return sortId;
  if (LEGACY_SORT_TO_NAME_ASC.has(sortId)) {
    return EVENT_WEEKLY_SORT.STAFF_NAME_ASC;
  }
  return EVENT_WEEKLY_SORT.STAFF_NAME_ASC;
};

/** @param {string|Date} requestedDate */
const formatDateKey = (requestedDate) => {
  if (
    typeof requestedDate === "string" &&
    /^\d{4}-\d{2}-\d{2}/.test(requestedDate)
  ) {
    return requestedDate.slice(0, 10);
  }
  const d = new Date(requestedDate);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/**
 * Builds a stable key for bulk-select.
 * When `userId` is passed (staff-name weekly view), each staff card is independent.
 * Omit `userId` for calendar views where selection is event+date only.
 * Pass `null` for unassigned cards so they stay distinct from staff rows.
 * @param {number|string} eventId
 * @param {string|Date} requestedDate
 * @param {number|string|null} [userId]
 */
export const getEventShiftSelectionKey = (eventId, requestedDate, userId) => {
  const dateKey = formatDateKey(requestedDate);
  if (userId === undefined) {
    return `${eventId}_${dateKey}`;
  }
  const userKey = userId == null || userId === "" ? "unassigned" : userId;
  return `${eventId}_${userKey}_${dateKey}`;
};

/**
 * Staff-scoped keys look like `id_userId_date` (not the calendar `id_date` key).
 * @param {string} key
 * @param {number|string} eventId
 * @param {string} dateKey
 */
const isStaffScopedSelectionKey = (key, eventId, dateKey) => {
  const calendarKey = `${eventId}_${dateKey}`;
  if (!key || key === calendarKey) return false;
  return key.startsWith(`${eventId}_`) && key.endsWith(`_${dateKey}`);
};

/**
 * All selected keys that refer to the same event on the same date
 * (calendar key and any staff-scoped keys).
 * @param {Record<string, object>} selectedEvents
 * @param {number|string} eventId
 * @param {string|Date} requestedDate
 * @returns {string[]}
 */
export const getRelatedEventShiftSelectionKeys = (
  selectedEvents,
  eventId,
  requestedDate,
) => {
  if (!selectedEvents || eventId == null) return [];
  const dateKey = formatDateKey(requestedDate);
  if (!dateKey) return [];
  const calendarKey = `${eventId}_${dateKey}`;
  const keys = [];
  if (selectedEvents[calendarKey]) keys.push(calendarKey);
  Object.keys(selectedEvents).forEach((key) => {
    if (isStaffScopedSelectionKey(key, eventId, dateKey)) {
      keys.push(key);
    }
  });
  return keys;
};

/**
 * Whether an event/shift should show as selected in the current view.
 * Bridges calendar (`id_date`) and staff (`id_userId_date`) key formats so
 * selections persist when switching Daily / Weekly / Monthly layouts.
 * @param {Record<string, object>} selectedEvents
 * @param {number|string} eventId
 * @param {string|Date} requestedDate
 * @param {number|string|null} [userId] Pass when rendering a staff-scoped card.
 */
export const isEventShiftSelected = (
  selectedEvents,
  eventId,
  requestedDate,
  userId,
) => {
  if (!selectedEvents || eventId == null) return false;
  const dateKey = formatDateKey(requestedDate);
  if (!dateKey) return false;

  const calendarKey = `${eventId}_${dateKey}`;
  if (userId !== undefined) {
    const staffKey = getEventShiftSelectionKey(eventId, requestedDate, userId);
    return Boolean(selectedEvents[staffKey] || selectedEvents[calendarKey]);
  }

  return (
    getRelatedEventShiftSelectionKeys(selectedEvents, eventId, requestedDate)
      .length > 0
  );
};
